import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { apiFetch } from "@/lib/api";
import { buildSalesOrderOverviewPath } from "@/lib/hubEntityDetailPaths";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

interface SalesOrderCancelEligibility {
  allowed: boolean;
  reason: string | null;
  batchCount: number;
}

export function SalesOrderDetailOverflowMenu({
  orderId,
  orderNumber,
  mvId,
  isPendingOrder,
  isAdmin,
}: {
  orderId: string;
  orderNumber: string;
  mvId: string | null;
  isPendingOrder: boolean;
  isAdmin: boolean;
}): JSX.Element | null {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const overflowMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [overflowMenuBox, setOverflowMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const showMenu = isAdmin && isPendingOrder && mvId == null;

  const eligibilityQuery = useQuery({
    queryKey: ["sales-orders", "cancel-eligibility", orderId],
    queryFn: () =>
      apiFetch<{ eligibility: SalesOrderCancelEligibility }>(
        `/api/sales-orders/${encodeURIComponent(orderId)}/cancel/eligibility`,
      ).then((response) => response.eligibility),
    enabled: showMenu,
    staleTime: 15_000,
  });

  const updateOverflowMenuPosition = useCallback(() => {
    const trigger = overflowMenuButtonRef.current;
    if (trigger == null) {
      setOverflowMenuBox(null);
      return;
    }
    setOverflowMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 220,
        horizontalAlign: "end",
        maxHeightCap: 240,
      }),
    );
  }, []);

  useEffect(() => {
    if (!overflowMenuOpen) {
      return;
    }
    updateOverflowMenuPosition();
    const onScrollOrResize = () => updateOverflowMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [overflowMenuOpen, updateOverflowMenuPosition]);

  useEffect(() => {
    if (!overflowMenuOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (overflowMenuButtonRef.current?.contains(target)) {
        return;
      }
      setOverflowMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [overflowMenuOpen]);

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean }>(`/api/sales-orders/${encodeURIComponent(orderId)}/cancel`, {
        method: "POST",
      }),
    onSuccess: async () => {
      setCancelError(null);
      setIsCancelModalOpen(false);
      setOverflowMenuOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["sales-orders", "detail", orderId] }),
        queryClient.invalidateQueries({ queryKey: ["sales-orders", "cancel-eligibility", orderId] }),
      ]);
      navigate(buildSalesOrderOverviewPath(orderNumber));
    },
    onError: (error) => {
      setCancelError(error instanceof Error ? error.message : "Could not cancel sales order.");
    },
  });

  if (!showMenu) {
    return null;
  }

  const eligibility = eligibilityQuery.data;
  const cancelBlockedReason =
    eligibility?.allowed === false ? eligibility.reason : eligibilityQuery.isError ? "Could not check cancel eligibility." : null;

  function openCancelModal() {
    setCancelError(null);
    setOverflowMenuOpen(false);
    setIsCancelModalOpen(true);
  }

  return (
    <>
      <Button
        ref={overflowMenuButtonRef}
        type="button"
        variant="secondary"
        aria-label="More sales order actions"
        aria-haspopup="menu"
        aria-expanded={overflowMenuOpen}
        onClick={() => setOverflowMenuOpen((open) => !open)}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </Button>
      {overflowMenuOpen && overflowMenuBox != null
        ? createPortal(
            <div
              className="fixed overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: overflowMenuBox.left,
                top: overflowMenuBox.top,
                width: overflowMenuBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              onPointerDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm",
                  cancelBlockedReason ? "cursor-not-allowed text-slate-400" : "text-red-700 hover:bg-slate-50",
                )}
                disabled={Boolean(cancelBlockedReason) || eligibilityQuery.isLoading}
                title={cancelBlockedReason ?? "Cancel this pending hub order and keep the GSO number"}
                onClick={() => {
                  if (cancelBlockedReason) {
                    return;
                  }
                  openCancelModal();
                }}
              >
                Cancel order
              </button>
            </div>,
            document.body,
          )
        : null}
      {isCancelModalOpen ? (
        <DocumentModal
          title="Cancel sales order"
          description={
            <>
              Cancel {orderNumber}? The order stays in the hub as Cancelled so this GSO number is never reused.
            </>
          }
          onClose={() => {
            if (!cancelMutation.isPending) {
              setCancelError(null);
              setIsCancelModalOpen(false);
            }
          }}
          shellClassName="max-w-lg"
        >
          <div className="space-y-4">
            {cancelBlockedReason ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {cancelBlockedReason}
              </div>
            ) : null}
            {cancelError ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{cancelError}</div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={cancelMutation.isPending}
                onClick={() => {
                  setCancelError(null);
                  setIsCancelModalOpen(false);
                }}
              >
                Keep order
              </Button>
              <Button
                type="button"
                variant="danger"
                isLoading={cancelMutation.isPending}
                disabled={Boolean(cancelBlockedReason)}
                onClick={() => cancelMutation.mutate()}
              >
                Cancel order
              </Button>
            </div>
          </div>
        </DocumentModal>
      ) : null}
    </>
  );
}
